import React from 'react';
import { Link } from 'react-router-dom';
import { contactLink } from '../../components/ContactForm';

// The playtest covers all six climbs. ENDINGS_VIDEO_ID: the six climbs'
// farewells as one film (vertigo `npm run endings`), on YouTube; the figure
// shows while it's set. The final form opens after the Long Fall (the last
// climb in order).
const ENDINGS_VIDEO_ID = 'C85S-vQijgY';

const PLAYTEST_URL = 'https://vertigo-climb.netlify.app/?playtest=1';
const FRESH_URL = 'https://vertigo-climb.netlify.app/?playtest=new';
const OFF_URL = 'https://vertigo-climb.netlify.app/?playtest=0';
const FEEDBACK_FORM = contactLink('Vertigo playtest');

const post = {
  slug: 'vertigo-playtest',
  title: 'Play Vertigo with us: six climbs, an open playtest through November',
  date: '2026-11-01',
  cover: '/blog/vertigo-playtest/long-fall.jpg',
  coverAlt:
    'Morning in a horseshoe of sheer sandstone cliffs: a stair cut up the rock on the left, a tall arched aqueduct ahead with thin waterfalls falling behind its arches into mist, palms in the foreground',
  coverCaption: 'The Long Fall, from the ledge where it starts.',
  excerpt:
    'Vertigo has three new climbs: a descent into the founders’ copper pit, a crossing of the range by the old tin road, and a climb up a waterfall with no water. Throughout November we are running an open playtest of all six. No sign-up, nothing to install.',
  content: (
    <>
      <p>
        When we <Link to="/blog/vertigo">first wrote about Vertigo</Link> in
        September it had three climbs: sandstone pillars above the clouds, a
        drowned city in a storm and a monks&apos; crag at dawn. It now has
        six, and before we go any further we want to watch people who are
        not us play them. So throughout November we are running an open
        playtest, and you are invited. There is nothing to sign up for and
        nothing to install.
      </p>

      {ENDINGS_VIDEO_ID && (
        <figure className="post-figure">
          <div className="post-video">
            <iframe
              src={`https://www.youtube-nocookie.com/embed/${ENDINGS_VIDEO_ID}`}
              title="Vertigo: how each of the six climbs ends"
              loading="lazy"
              allow="accelerometer; encrypted-media; gyroscope; picture-in-picture; fullscreen"
              allowFullScreen
            />
          </div>
          <figcaption>
            How each of the six climbs ends, filmed by the game&apos;s own
            camera: the real goal, reached in the game, and the farewell it
            plays.
          </figcaption>
        </figure>
      )}

      <h2>Down, for once</h2>
      <p>
        The bell on the Pillars was cast from copper, and the copper came out
        of the ground. <strong>The Founders&apos; Pit</strong> is where. It is
        a square mine worked down in rings like a stepwell, each one smaller
        than the one above, and at the very bottom, 180 metres down, the
        founders&apos; furnace sits banked and glowing. The first three climbs
        go up. This one goes down, from dusk at the rim into the night.
      </p>
      <p>
        Every ring is a puzzle you have to work out before you can go on, and
        each one is a machine: a hoist balanced against a cistern, basins of
        borrowed water, galleries of gates, a flooded shaft, heaps of old
        spoil. Halfway down you pass through a layer of cloud with a small
        chapel cut into the rock beneath it. Seven cast plates on the way
        tell you who dug it, going back in time the deeper you get.
      </p>

      <div className="post-photo-grid">
        <img
          src="/blog/vertigo-playtest/cloud.jpg"
          alt="Cloud filling the middle of the pit between brick walls, the benches and their rails stepping down below it"
        />
        <img
          src="/blog/vertigo-playtest/furnace.jpg"
          alt="The founders' furnace on the pit floor, a brick chimney glowing orange at the top, a water wheel beside it"
        />
      </div>

      <p>
        Light the furnace and its fire climbs the whole pit in a column you
        can see from the rim. If you would rather not climb back out, there
        is a cage.
      </p>

      <figure className="post-figure">
        <img
          src="/blog/vertigo-playtest/column.jpg"
          alt="Looking straight up a deep square pit of brick benches, a column of orange firelight rising through drifting cloud to the evening sky"
        />
        <figcaption>The Founders&rsquo; Pit, once the fire is lit.</figcaption>
      </figure>

      <figure className="post-figure">
        <img
          src="/blog/vertigo-playtest/headframe.jpg"
          alt="An iron headframe with a lamp at its top standing over the pit at the end of a long railed gantry, against an evening sky"
        />
        <figcaption>The headframe. The way home, once you have earned it.</figcaption>
      </figure>

      <h2>Then out, over the range</h2>
      <p>
        The bell was copper and tin, and the tin came from over the
        mountains. <strong>The Tinners&apos; Col</strong> starts the morning
        after the Pit, at first light, at a boathouse on a still lake under a
        wall of peaks. You paddle up the lake, climb the pale face at its
        head, go through a window in the rock and up to the col, where a
        bronze glass has stood unturned since the road closed. It is 177
        metres up.
      </p>

      <div className="post-photo-grid">
        <img
          src="/blog/vertigo-playtest/col-dock.jpg"
          alt="A timber dock at first light with two canoes, one red and one orange, on a still lake, a pale limestone face at its head and snowy peaks to the left"
        />
        <img
          src="/blog/vertigo-playtest/col-crest.jpg"
          alt="A snowy crest high on a mountain col in the morning, a stone parapet along its edge, jagged snow peaks and dark pine forest beyond, a cable running down from a small station"
        />
      </div>

      <h2>Last, a waterfall with no water</h2>
      <p>
        The founders&apos; furnace was blown by bellows, and the bellows were
        worked by a wheel in the valley. <strong>The Long Fall</strong> is
        where its water came from: a river that fell a thousand metres off a
        mountain into the forest, dry since the weir at its head was shut.
        You start on a ledge 40 metres up a horseshoe of cliffs, in the low
        morning sun, with one waterfall still falling in front of you and
        swifts flying in and out of it. The rim is 250 metres up. The river
        is somewhere behind it.
      </p>
      <p>
        On the way up you go behind that waterfall, then up terraced gardens
        whose pools fill and drain as you send the water along the rim from
        one sluice to the next, ride a float up a flooded shaft, cross a
        long aqueduct that has never carried water, and climb through a maze
        of black rock towers whose way through is marked by the
        waterkeepers&apos; lit cairns, if you can read them from the
        aqueduct first. Seven knotted cords on the way tell you who kept the
        water, and how.
      </p>

      <div className="post-photo-grid">
        <img
          src="/blog/vertigo-playtest/veil.jpg"
          alt="Behind a waterfall on a narrow railed gallery, the falling sheet of water filling the view and a faint rainbow arcing through the spray, a brick wall on the left"
        />
        <img
          src="/blog/vertigo-playtest/threads.jpg"
          alt="Terraced gardens of pale stone stepped up the foot of a towering sandstone wall, a railed stair climbing between them and thin threads of water falling from far above"
        />
      </div>

      <figure className="post-figure">
        <img
          src="/blog/vertigo-playtest/labyrinth.jpg"
          alt="Black rock towers in a row below a sandstone cliff, their railed tops linked by ladders and bridges, a warm lamp glowing on a cairn on each landing, a thin waterfall down the cliff and the aqueduct's railed deck in front"
        />
        <figcaption>The towers from the aqueduct. One cairn on every landing of the way through.</figcaption>
      </figure>

      <p>
        At the top you will have to work out how to get the river back. It
        is the last of the six, so if you get there, we would love to know
        how it felt.
      </p>

      <h2>What this playtest is for</h2>
      <p>
        Every climb is there, start to finish. What we want now is to make
        them as good as they can be, which means the unglamorous things: the
        ledge you were sure you could reach, the lever you pulled six times
        without knowing why, the rest stop where you stood wondering where to
        go, the frame rate on the laptop you actually own. And heights. If
        looking down bothers you, we especially want to hear from you.
      </p>
      <p>
        We have had a head start on the Long Fall. Before writing this we
        sent nine blind testers up it, three rounds of three: AI agents told
        nothing about the level, playing it with real key presses and
        looking only at what the screen showed. They walked off edges that
        looked like steps, followed hints past the puzzle the hint was for,
        and hunted for a lever that stood off to one side of the way. We
        fixed each round before sending the next, and the third fell off
        nothing. They are not people, though, and people are what November
        is for.
      </p>

      <h2>How to take part</h2>
      <ul>
        <li>
          You need a desktop or laptop browser, a mouse and keyboard, sound on,
          and two or three hours for all six. You do not have to do them in one
          sitting: the session sticks to your browser, so coming back tomorrow
          carries on the same playtest. Phones get a polite card asking for a computer.
        </li>
        <li>
          Open the playtest link:{' '}
          <a href={PLAYTEST_URL}>vertigo-climb.netlify.app/?playtest=1</a>.
          The ordinary link works as a game but sends us nothing, so please use
          this one. The title card says exactly what it records before you
          start.
        </li>
        <li>
          WASD to move, mouse to look, Space to jump, E to use things, R
          twice to start the climb over, M for music, Esc to pause. If it feels choppy,
          Settings has a quality option.
        </li>
        <li>
          Play it the way you would play any game, and please do not look
          anything up. Getting stuck is useful to us, not a failure.
        </li>
      </ul>

      <h2>What the game records</h2>
      <p>
        The playtest link switches on a small recorder in the game. It notes
        the places you reach and when, what you use and pick up, which hints
        appear, where you fall, where you stand every few seconds, your frame
        rate, your browser and the name of your graphics chip, and any
        errors. Time only counts while you are playing, so stopping for tea
        never looks like being stuck. No name, no email, no account. Our host,
        Netlify, also sees your IP address when the log arrives, as any
        website would. The log is sent at the end of each climb, when you
        close the tab, and with your feedback. Play for less than half a
        minute and nothing is sent at all.
      </p>

      <h2>Telling us what you thought</h2>
      <ul>
        <li>
          <strong>Rate each climb.</strong> The results card at the end of
          every climb has a one-to-five rating and room for a short note.
        </li>
        <li>
          <strong>Stop whenever you like.</strong> Press Esc and choose{' '}
          <em>I&rsquo;m done: send feedback</em>. Giving up halfway tells us as
          much as finishing.
        </li>
        <li>
          <strong>A few questions at the end.</strong> After the last climb a
          short form opens by itself: where you got stuck, whether the hints
          helped, whether the heights got to you, how it ran, and the moment
          you remember.
        </li>
      </ul>

      <div className="post-callout">
        <h3>If something goes wrong</h3>
        <p>
          If the game says it could not send your feedback, press Esc and
          choose <em>Download my log</em>, then attach the file to a message
          through our <Link to={FEEDBACK_FORM}>contact form</Link>. The form is
          also the place for a screenshot of a bug, or a single sentence like
          &ldquo;I fell off the same ledge nine times&rdquo;.
        </p>
        <p>
          Sharing a computer? Have the next person open{' '}
          <a href={FRESH_URL}>?playtest=new</a> so their session starts fresh.
          To switch the recorder off, open <a href={OFF_URL}>?playtest=0</a>.
        </p>
      </div>

      <h2>What happens next</h2>
      <p>
        We will read everything that comes in and fix as we go, so the climb
        you play at the end of November may be a little different from the
        one at the start. When the playtest wraps up we will write here about
        what we learned, the good and the embarrassing.
      </p>

      <figure className="post-figure">
        <img
          src="/blog/vertigo-playtest/under-the-cloud.jpg"
          alt="Looking up from inside the pit through the underside of the cloud to a square of dusk sky"
        />
        <figcaption>From the chapel, looking back up. You came down all of that.</figcaption>
      </figure>

      <p>
        <a href={PLAYTEST_URL}>Open the playtest</a> and start climbing. Do look
        down.
      </p>
      <p className="post-signoff">Thanks for helping us make it better.</p>
    </>
  ),
};

export default post;
